/**
 * @fileoverview ShoppingItems Component
 *
 * This component renders a list of shopping cart items with different states:
 * - Loading state with spinner
 * - Error state with retry functionality
 * - Empty cart state with optional reset button
 * - Items list with individual ShoppingItem components
 *
 * Supports both interactive and readonly modes for different contexts
 * (cart view vs checkout review).
 */

import React from "react";
import { Circle } from "better-react-spinkit";
import { ShoppingItem } from "./ShoppingItem";
import { IoMdCart } from "react-icons/io";
import { ShoppingCartItem } from "../ShoppingCart.types";
import "./ShoppingItems.scss";

/**
 * Props interface for ShoppingItems component
 */
interface ShoppingItemsProps {
  /** Array of cart items to display */
  items: ShoppingCartItem[];
  /** Whether cart data is currently loading */
  loading: boolean;
  /** Error message if loading failed */
  error: string | null;
  /** Callback when item quantity is updated */
  onUpdateQuantity: (id: number, quantity: number) => void;
  /** Callback when item is removed from cart */
  onRemoveItem: (id: number) => void;
  /** Optional callback to reset/clear the entire cart */
  onResetCart?: () => void;
  /** Callback to retry loading cart data after error */
  onRetry: () => void;
  /** Whether items should be displayed in readonly mode (no interaction) */
  readonly?: boolean;
}

/**
 * ShoppingItems Component
 *
 * Renders the shopping cart items list with appropriate states for loading,
 * errors, empty cart, and populated cart. Handles different display modes
 * for cart view and checkout review.
 *
 * @param props - Component props including items, state, and callbacks
 * @returns JSX element representing the cart items display
 */
const ShoppingItems: React.FC<ShoppingItemsProps> = ({
  items,
  loading,
  error,
  onUpdateQuantity,
  onRemoveItem,
  onResetCart,
  onRetry,
  readonly = false,
}) => {
  const hasItems = items.length > 0;

  // Loading state: Show spinner and loading message
  if (loading) {
    return (
      <div className="LoadingCart" data-testid="cart-loading">
        <Circle size={50} color="#ff5a00" scaleStart={0.4} scaleEnd={0.8} />
        <p>Loading basket...</p>
      </div>
    );
  }

  // Error state: Show error message with retry option
  if (error) {
    return (
      <div className="ErrorCart" data-testid="cart-error">
        <p>Error loading basket: {error}</p>
        <button onClick={onRetry}>Retry</button>
      </div>
    );
  }

  // Empty cart state: Show empty cart icon and optional reset button
  if (!hasItems) {
    return (
      <div className="EmptyCart" data-testid="cart-empty">
        <div className="EmptyCartIcon">{IoMdCart({ size: "80px" })}</div>
        <p>Your cart is empty</p>
        {onResetCart && (
          <button className="ResetCartButton" onClick={onResetCart}>
            Reset Cart
          </button>
        )}
      </div>
    );
  }

  // Items list: Render individual ShoppingItem components
  return (
    <>
      {items.map((item) => (
        <ShoppingItem
          key={item.id}
          item={item}
          onUpdateQuantity={onUpdateQuantity}
          onRemoveItem={onRemoveItem}
          readonly={readonly}
        />
      ))}
    </>
  );
};

export default ShoppingItems;
